import ConnectionModel, {
  WebhookJsonTemplateModel,
} from '@mm/state/model/ConnectionModel';
import { Heading, Tooltip } from '@mm/components';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppstoreAddOutlined } from '@ant-design/icons';
import styles from './EditJSONWebhookIntegrationSlideoutEditor.module.css';

import { selectWebhookJsonTemplates } from '@mm/state/selectors/connectionsSelectors';
import classNames from 'classnames';

export interface JSONWebhookTemplateProps {
  connection: ConnectionModel;
  updateConnection: (updatedFields: Partial<ConnectionModel>) => void;
  setJsonEditorVisible: (visible: boolean) => void;
}

export const JSONWebhookTemplates = ({
  connection,
  updateConnection,
  setJsonEditorVisible,
}: JSONWebhookTemplateProps) => {
  const webhookJsonTemplates = useSelector(selectWebhookJsonTemplates);
  const { retriveWebhookJsonTemplates } = useConnectionCRUDActions();

  const [isTemplatesSelectOpen, setIsTemplatesSelectOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<{
    title: string;
    imageUrl: string;
  } | null>(null);

  const visibleJsonTemplatesList = webhookJsonTemplates?.slice(0, 5);
  const dropdownJsonTemplatesList = webhookJsonTemplates?.slice(5);

  useEffect(() => {
    if (!webhookJsonTemplates?.length) {
      retriveWebhookJsonTemplates();
    }
  }, []);

  const onTemplateClick = (template: WebhookJsonTemplateModel) => {
    setJsonEditorVisible(true);
    updateConnection({
      requestHeaders: JSON.parse(template.requestHeaders),
      requestBody: JSON.parse(template.requestBody),
      url: connection?.url || template.url,
      requestType: connection?.url
        ? connection.requestType
        : template.requestType,
    });
  };

  if (!webhookJsonTemplates?.length) {
    return null;
  }

  return (
    <>
      <Heading text="Templates:" isBold size={5} />
      <div className={styles.jsonTemplatesBlock}>
        {visibleJsonTemplatesList?.map(template => (
          <Tooltip key={template.title} title={template.title}>
            <div
              className={styles.jsonTemplateBox}
              onClick={() => onTemplateClick(template)}
            >
              <img
                src={template.imageUrl}
                alt={template.title}
                className={styles.jsonTemplateImage}
              />
              <div className={styles.jsonTemplateTitle}>{template.title}</div>
            </div>
          </Tooltip>
        ))}
        {!!dropdownJsonTemplatesList?.length && (
          <button
            role="combobox"
            aria-label="Select template"
            aria-haspopup="listbox"
            aria-expanded={isTemplatesSelectOpen}
            aria-controls="select-dropdown"
            className={classNames(
              styles.jsonTemplateBox,
              styles.templatesSelect,
            )}
            onClick={() => {
              setIsTemplatesSelectOpen(!isTemplatesSelectOpen);
            }}
          >
            <div className={styles.templatesSelect__selected}>
              {selectedTemplate ? (
                <img
                  src={selectedTemplate.imageUrl}
                  alt={selectedTemplate.title}
                  className={styles.templatesSelect__selected_image}
                />
              ) : (
                <AppstoreAddOutlined
                  style={{ color: '#597EF7', fontSize: '32px' }}
                />
              )}
              <span className={styles.templateSelect__template_title}>
                {selectedTemplate?.title || 'Other'}
              </span>
            </div>

            <span className={styles.templatesSelect__arrow} />
            <ul
              role="listbox"
              id="select-dropdown"
              className={styles.templatesSelect__dropdown}
            >
              {dropdownJsonTemplatesList.map(template => (
                <li
                  role="option"
                  aria-selected={selectedTemplate?.title === template.title}
                  key={template.title}
                  className={styles.templatesSelect__dropdownItem}
                  onClick={() => {
                    setSelectedTemplate(template);
                    setIsTemplatesSelectOpen(false);
                    onTemplateClick(template);
                  }}
                >
                  <img
                    src={template.imageUrl}
                    alt={template.title}
                    className={styles.templatesSelect__dropdownItem_image}
                  />
                  <div className={styles.templateSelect__template_title}>
                    {template.title}
                  </div>
                </li>
              ))}
            </ul>
          </button>
        )}
      </div>
    </>
  );
};
